<template>
  <!-- Error message above the buttons -->
  <div v-if="showDownloadError" class="mb-2 font-bold text-error-600">
    {{ t('patterns.download.error_fetching_url') }}
  </div>

  <button
    v-if="(patternPurchasable && canDownloadImmediately) || !patternPurchasable"
    :class="buttonClass"
    :disabled="showDownloadError || isLoading"
    @click="buttonClickHandler"
  >
    <span v-if="isLoading" class="mr-2 flex items-center justify-center">
      <svg class="size-5 animate-spin text-white" viewBox="0 0 24 24">
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
          fill="none"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
      </svg>
    </span>
    <span
      v-if="!isLoading"
      class="custom-label flex items-center gap-1 text-sm sm:text-base"
    >
      <IconDownload class="text-2xl" />{{ buttonLabel }}
    </span>
    <span
      v-if="!isLoading && patternPurchasable && canDownloadImmediately"
      class="custom-label mt-1 text-sm font-normal"
    >
      {{ t('patterns.download.already_purchased') }}
    </span>
  </button>

  <Modal v-model:active="showPatternDownloadModal" size="lg">
    <template #body>
      <div
        class="mx-2 mb-5 flex items-start justify-center gap-2 pt-base text-center"
      >
        <form
          class="m-4 flex flex-col items-center gap-4"
          @submit.prevent="onNewsletterSignupFormSubmit"
        >
          <div class="mx-auto max-w-lg text-sm">{{ productName }}</div>
          <h2 class="mb-4 text-xl font-bold">
            {{ t('patterns.download.signup_get_free') }}
          </h2>
          <div class="max-w-lg text-sm">
            {{ t('patterns.download.modal_description1') }}
          </div>
          <div class="m-2 max-w-lg text-sm font-bold">
            {{ t('patterns.download.modal_description2') }}
          </div>
          <div class="max-w-lg text-sm">
            {{ t('patterns.download.modal_description3') }}
          </div>

          <div class="flex w-full flex-col justify-evenly gap-4 md:flex-row">
            <div class="relative h-10 w-full">
              <input
                id="pattern-download-first-name"
                v-model="customerName"
                placeholder=""
                type="text"
                autocomplete="given-name"
                required
                class="peer block w-full appearance-none rounded border border-gray px-2.5 pb-1 pt-4 text-sm text-gray-900 transition focus:border-primary-600 focus:outline-none focus:ring-0"
              />
              <label
                for="pattern-download-first-name"
                class="absolute left-1 top-4 z-10 origin-[0] -translate-y-4 scale-75 px-2 text-sm text-gray-500 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-4 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2"
                >{{ t('patterns.download.name') }}</label
              >
            </div>
            <div class="relative flex w-full flex-col gap-2">
              <div class="relative h-10">
                <input
                  id="pattern-download-email"
                  v-model="customerEmail"
                  placeholder=""
                  type="email"
                  autocomplete="email"
                  required
                  class="peer block w-full appearance-none rounded border border-gray px-2.5 pb-1 pt-4 text-sm text-gray-900 transition focus:border-primary-600 focus:outline-none focus:ring-0"
                  :class="{
                    'border-error-200 bg-error-100 text-error-800':
                      emailIsValid === false
                  }"
                  @blur="validateEmailInput"
                  @input="validateEmailInput"
                />
                <label
                  for="pattern-download-email"
                  class="absolute left-1 top-4 z-10 origin-[0] -translate-y-4 scale-75 px-2 text-sm text-gray-500 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-4 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2"
                  >{{ t('patterns.download.email') }}</label
                >
              </div>
              <div v-if="emailIsValid === false" class="text-error">
                <ul role="list" class="space-y-1 text-left text-xs">
                  <li>
                    {{ t('global.errors.form_validation.invalid_email') }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="col-span-2 w-full">
            <button
              type="submit"
              class="flex w-full justify-center rounded bg-green px-3 py-2 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-green-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:pointer-events-none disabled:opacity-50"
              :disabled="!formIsValid"
            >
              {{ t('patterns.download.signup') }}
            </button>
          </div>
          <div class="col-span-2 text-xs">
            {{ t('patterns.download.tos_link_description') }}
            <a
              href="/policies/privacy-policy"
              target="_blank"
              class="text-primary decoration-solid hover:text-pink"
              >{{ t('patterns.download.tos_link_text') }}</a
            >
          </div>
          <div class="col-span-2 text-xs">
            {{ t('patterns.download.already_signed_up') }}
            <a
              href="#"
              class="text-primary decoration-solid hover:text-pink"
              @click="alreadySubscribedDownload"
              >{{ t('patterns.download.download_pattern_here') }}</a
            >
          </div>
        </form>
      </div>
    </template>
  </Modal>

  <Modal v-model:active="showThankYouModal" :close-button="false">
    <template #body>
      <div
        class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0"
      >
        <div class="">
          <div>
            <div
              class="mx-auto flex size-12 items-center justify-center rounded-full bg-green-100"
            >
              <svg
                class="size-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4.5 12.75l6 6 9-13.5"
                />
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-5">
              <h3
                id="modal-title"
                class="text-base font-semibold leading-6 text-gray-900"
              >
                {{ t('patterns.download.success_title') }}
              </h3>
              <div class="mt-2">
                <div class="flex flex-col gap-2">
                  <p class="text-sm text-gray-500">
                    {{ t('patterns.download.success_content.sentence1') }}
                  </p>
                  <p class="text-sm text-gray-500">
                    {{ t('patterns.download.success_content.sentence2') }}
                  </p>
                  <br />
                  <p class="text-sm text-gray-500">
                    {{ t('patterns.download.success_content.sentence3') }}
                  </p>
                </div>
                <div class="mt-5 text-sm text-gray-500">
                  {{
                    t('patterns.download.countdown', {
                      countdown: countdownValue
                    })
                  }}
                </div>
                <button
                  class="mt-5 inline-block text-sm text-primary decoration-solid hover:text-pink"
                  @click="startDownload"
                >
                  {{ t('patterns.download.download_pattern_here') }}
                </button>
              </div>
            </div>
          </div>
          <div class="mt-5 sm:mt-6">
            <button
              class="btn-md w-full bg-primary text-white"
              @click="showThankYouModal = false"
            >
              {{ t('patterns.download.success_close') }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue';

import IconDownload from '@/shared/components/UI/Icons/IconDownload.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import { useTranslations } from '@/shared/composables/useTranslations';
import { campaignType } from '@/shared/constants/campaignTypes';
import { userCookies } from '@/shared/constants/cookies';
import { useProductStore } from '@/shared/pinia/modules/product';
import { useUserStore } from '@/shared/pinia/modules/user';
import { getCookie, setCookie } from '@/shared/utils/cookie';
import { isEmailValid } from '@/shared/utils/email';
import { generateContactUuid } from '@/shared/utils/emarsys';
import {
  hashUserData,
  trackDownloadPattern,
  trackHobbiiEvent
} from '@/shared/utils/tracking';
import {
  hobbiiLocalStorage,
  hobbiiSessionStorage
} from '@/shared/utils/web-storage';

const { t } = useTranslations();

const props = defineProps<{
  productName: string;
  productSku: string;
  patternPurchasable: boolean;
}>();

const productStore = useProductStore();
const userStore = useUserStore();

// --- State for modals and download logic ---
const showPatternDownloadModal = ref(false);
const showThankYouModal = ref(false);
const countdownValue = ref(0);

// --- Tracks if the authenticated user has previously purchased the paid pattern ---
const hasPreviousPurchase = ref(false);

// --- User input for newsletter signup (free patterns) ---
const customerEmail = ref('');
const customerName = ref('');

// --- Determines if the user can immediately download the pattern ---
// For paid patterns: true if authenticated and has previous purchase
// For free patterns: true if user has signed up for the newsletter
const canDownloadImmediately = computed(() => {
  if (props.patternPurchasable) {
    return userStore.isLogged && hasPreviousPurchase.value;
  } else {
    return isSignedUp();
  }
});

const emailIsValid = ref<boolean | null>(null);

const formIsValid = computed(() => emailIsValid.value && customerName.value);

const emarsysUUIDKey = 'emarsysUUID';

const emarsysUUID = computed(() => {
  if (userStore.isLogged && userStore.customer?.email) {
    return generateContactUuid(userStore.customer.email);
  } else {
    return hobbiiLocalStorage.getItem(emarsysUUIDKey);
  }
});

// --- On mount, update newsletter signup state and check for previous purchase (paid patterns) ---
onBeforeMount(async () => {
  await updateNewsletterSignupState();
  // Only check for download URL and hide price if this is a paid pattern and user is authenticated
  if (props.patternPurchasable && userStore.isLogged) {
    const url = await getPatternDownloadUrl();
    if (url) {
      hasPreviousPurchase.value = true;
      productStore.purchasable = false;
      const priceBlock =
        document.querySelector<HTMLElement>('.hb-pp-price-block');
      if (priceBlock) {
        priceBlock.hidden = true;
      }
    }
  }
  // Restore session storage logic for thank you modal
  if (
    hobbiiSessionStorage.getItem('signupForm') ===
    'pattern-download-newsletter-signup-form'
  ) {
    setAsSignedUp(); // Safeguard: ensure cookie is set after reload
    showThankYouDialog();
    hobbiiSessionStorage.removeItem('signupForm');
  }
});

async function getPatternDownloadUrl(): Promise<string | null> {
  return await productStore.patternDownloadUrl(
    props.productSku,
    emarsysUUID.value || '',
    getCookie('localization') || ''
  );
}

async function updateNewsletterSignupState(): Promise<void> {
  if (userStore.isLogged) {
    setAsSignedUp();
  } else if (!emarsysUUID.value) {
    // We need to prompt them for their email again so we can track the pattern
    // download. (They can always skip it by clicking "already signed up")
    setAsNotSignedUp();
  }
}

watch(showPatternDownloadModal, (newVal) => {
  trackHobbiiEvent(newVal ? 'show popup' : 'close popup', {
    category: `pattern content locking ${campaignType.HOBBII}`,
    label: props.productName
  });
});

const alreadySubscribedDownload = () => {
  trackHobbiiEvent('clicked on already a subscriber', {
    category: `pattern content locking ${campaignType.HOBBII}`,
    label: props.productName
  });
  setAsSignedUp(); // To avoid annoying users by asking for email every time
  startDownload();
};

// --- Error state for download failures ---
const showDownloadError = ref(false);
// --- Loading state for download action ---
const isLoading = ref(false);

const startDownload = async () => {
  isLoading.value = true;
  showDownloadError.value = false;
  const url = await getPatternDownloadUrl();
  isLoading.value = false;
  if (!url) {
    showDownloadError.value = true;
    return;
  }
  location.href = url;
  closeDownloadModal();

  trackDownloadPattern({
    category: props.patternPurchasable ? 'plus_pattern' : 'free_pattern',
    action: 'download_pattern',
    label: props.productName,
    item_id: productStore.legacyId || ''
  });
};

// --- Handles download button click for free patterns (newsletter-gated) ---
const handleDownloadButtonClick = () => {
  if (canDownloadImmediately.value) {
    startDownload();
  } else {
    openDownloadModal();
  }
};

const openDownloadModal = () => {
  showPatternDownloadModal.value = true;
};

const closeDownloadModal = () => {
  showPatternDownloadModal.value = false;
};

const validateEmailInput = (e: Event) => {
  // if user has typed invalid number before, use validation on keyup else: only validate on blur
  if (e.type === 'input' && emailIsValid.value !== false) {
    return;
  }

  // Hide error when field is empty
  if (!customerEmail.value) {
    emailIsValid.value = null;
  } else if (isEmailValid(customerEmail.value)) {
    emailIsValid.value = true;
  } else {
    emailIsValid.value = false;
  }
};

const onNewsletterSignupFormSubmit = async () => {
  const patternDownloadSignupForm = document.querySelector<HTMLFormElement>(
    '#pattern-download-newsletter-signup-form'
  );

  if (!patternDownloadSignupForm) return;

  const firstNameInput =
    patternDownloadSignupForm.querySelector<HTMLInputElement>(
      'input[name="contact[first_name]"]'
    );
  const emailInput = patternDownloadSignupForm.querySelector<HTMLInputElement>(
    'input[name="contact[email]"]'
  );
  const noteInput = patternDownloadSignupForm.querySelector<HTMLInputElement>(
    'input[name="contact[note][contact_uuid]"]'
  );
  if (firstNameInput) firstNameInput.value = customerName.value;
  if (emailInput) {
    emailInput.value = customerEmail.value;
    const contactUuid = generateContactUuid(emailInput.value);
    // Set the contact_uuid in the note field so we can lookup the customer in Shopify using the contact_uuid
    if (noteInput) noteInput.value = contactUuid;
    hobbiiLocalStorage.setItem(emarsysUUIDKey, contactUuid);
  }

  // Ensure the cookie is set before the form is submitted
  setAsSignedUp();
  hobbiiSessionStorage.setItem(
    'signupForm',
    'pattern-download-newsletter-signup-form'
  );

  // showThankYouDialog(); // Do not show here, will be handled on reload

  trackHobbiiEvent('pattern_download_signup', {
    category: 'newsletter',
    customer_name_hash:
      firstNameInput?.value && hashUserData(firstNameInput.value),
    customer_email_hash: emailInput?.value && hashUserData(emailInput.value),
    currency: window.Shopify.currency.active
  });

  trackHobbiiEvent('clicked on signup', {
    category: `pattern content locking ${campaignType.HOBBII}`,
    label: props.productName
  });

  window.posthog?.capture('User signs up for newsletter', {
    'Content Locking': campaignType.HOBBII,
    'Pattern Name': props.productName
  });

  patternDownloadSignupForm.submit();
};

function setAsSignedUp() {
  setCookie(userCookies.NEWSLETTER_SIGNUP, '1', 400);
}
function setAsNotSignedUp() {
  setCookie(userCookies.NEWSLETTER_SIGNUP, '0', 400);
}

// --- Checks if the user has signed up for the newsletter (free patterns) ---
function isSignedUp() {
  return getCookie(userCookies.NEWSLETTER_SIGNUP) === '1';
}

// Show newsletter signup success message if needed
function showThankYouDialog() {
  showThankYouModal.value = true;
  startCountdown(5, startDownload);
}

const startCountdown = (seconds: number, onDone: () => void) => {
  countdownValue.value = seconds;

  const countdownInterval = setInterval(() => {
    countdownValue.value--;

    if (countdownValue.value === 0) {
      clearInterval(countdownInterval);

      if (onDone) {
        onDone();
      }
    }
  }, 1000);
};

// --- Computed for unified download button ---
const buttonClass = computed(() =>
  props.patternPurchasable && canDownloadImmediately.value
    ? 'md:mt-0md:h-[72px] group mt-4 flex h-[82px] w-full cursor-pointer flex-col items-center justify-center rounded-lg bg-pink text-center text-lg font-bold text-white transition-all duration-300 ease-in-out hover:bg-pink-darker disabled:cursor-not-allowed disabled:opacity-50 md:text-base'
    : 'group flex h-14 w-full cursor-pointer items-center justify-center rounded-lg bg-primary text-center text-lg font-bold text-white transition-all duration-300 ease-in-out hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 md:text-base'
);

const buttonClickHandler = computed(() =>
  props.patternPurchasable && canDownloadImmediately.value
    ? startDownload
    : handleDownloadButtonClick
);

const buttonLabel = computed(() =>
  props.patternPurchasable && canDownloadImmediately.value
    ? t('patterns.download.download_pattern')
    : t('patterns.download.download_free_pattern')
);
</script>
